// ink-component
import React, { ReactElement, ReactNode } from 'react';
import { ThemeProvider } from 'styled-components';

import Avatar from '../Avatar/Avatar';
import Bubble from '../Bubble/Bubble';

import getGlobalProps from '../../helpers/constants/global-props';

import {
  MessageAttachments,
  MessageContentWrapper,
  MessageContentBubble,
  MessageInfo,
  MessageMetadata,
  MessageWrapper,
} from './styles';
import MessageAttachment from './components/MessageAttachment/MessageAttachment';
import MessageReply from './components/MessageReply/MessageReply';
import MessageScroller from './components/MessageScroller/MessageScroller';

const RoleVariants = {
  outbound: 'sender',
  inbound: 'receiver',
  carta: 'carta',
} as const;

export interface MessageProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Unique identifier */
  id?: string;
  /** New message indicator */
  isNew?: boolean;
  /** [Avatar](/components/Avatar) component */
  avatar: ReactElement<typeof Avatar>;
  /** Metadata content */
  metadata?: ReactNode;
  /** Highlight variant */
  roleVariant?: keyof typeof RoleVariants;
  /** Attachment(s) */
  attachments?: ReactElement<typeof MessageAttachment> | ReactElement<typeof MessageAttachment>[];
  /** Message content */
  children: ReactNode;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string;
}

/**
 * @purpose Messages are used to display comments or notes exchanged between multiple entities or users, offering context or updates regarding a specific task.
 * @status beta
 * @aka thread
 */
function Message(props: MessageProps) {
  const {
    className,
    style,
    attachments,
    avatar,
    children,
    id,
    isNew,
    metadata,
    roleVariant,
    'data-testid': dataTestId,
    ...otherProps
  } = props;

  const globalProps = getGlobalProps({});

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        messageRole: roleVariant,
      })}
    >
      <MessageWrapper id={id} {...globalProps} {...otherProps} data-testid={dataTestId}>
        <MessageInfo>
          {avatar && avatar}
          {isNew && <Bubble>New message</Bubble>}
        </MessageInfo>

        <MessageContentWrapper data-testid={dataTestId && `${dataTestId}-content-wrapper`}>
          {roleVariant !== 'carta' ? (
            <MessageContentBubble>
              {children}

              {attachments && (
                <MessageAttachments>{Array.isArray(attachments) ? attachments : [attachments]}</MessageAttachments>
              )}
            </MessageContentBubble>
          ) : (
            <>
              {children}

              {attachments && (
                <MessageAttachments>{Array.isArray(attachments) ? attachments : [attachments]}</MessageAttachments>
              )}
            </>
          )}

          <MessageMetadata>{metadata}</MessageMetadata>
        </MessageContentWrapper>
      </MessageWrapper>
    </ThemeProvider>
  );
}

Message.defaultProps = {
  roleVariant: 'outbound',
};

Message.displayName = 'Message';

Message.Attachment = MessageAttachment;
Message.Reply = MessageReply;
Message.Scroller = MessageScroller;

export default Message;
